/**
 * Pure helper for formatting timestamps into human-readable relative time strings.
 * An optional `now` timestamp can be provided for deterministic testing.
 *
 * The local calendar-day relationship between `value` and `now` is established
 * FIRST, before any elapsed-duration bucketing. This is DST-safe: a spring-forward
 * day has only 23 elapsed hours and a fall-back day has 25, so elapsed-duration
 * alone cannot reliably distinguish "today" from "yesterday" from "N days ago".
 * Elapsed-duration buckets (minutes/hours) are only used when `value` falls on
 * the same local calendar day as `now`.
 *
 * Thresholds:
 * - null/undefined => "Waiting for updates…"
 * - under 45 seconds or future clock skew => "Just now"
 * - 45–89 seconds => "1 minute ago"
 * - under 55 minutes => "{n} minutes ago"
 * - 55–89 minutes => "1 hour ago"
 * - 90 minutes+ and still the same local calendar day => "{n} hours ago"
 * - value's local calendar date is one day before now's => "Yesterday at HH:mm"
 * - value's local calendar date is two or more days before now's => "{n} days ago"
 */
export const formatRelativeTime = (value: Date | null | undefined, now?: Date): string => {
    if (!value) {
        return 'Waiting for updates…';
    }

    const current = now ?? new Date();
    const diffMs = current.getTime() - value.getTime();

    // Future clock skew or under 45 seconds
    if (diffMs < 0 || diffMs < 45 * 1000) {
        return 'Just now';
    }

    const diffSec = Math.floor(diffMs / 1000);

    // Local calendar-day relationship, computed before any elapsed-duration bucketing
    const nowDate = new Date(current.getFullYear(), current.getMonth(), current.getDate());
    const valueDate = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const calendarDaysDiff = Math.round((nowDate.getTime() - valueDate.getTime()) / (24 * 3600 * 1000));

    if (calendarDaysDiff > 0) {
        // Different local calendar day: always a day-scale label, regardless of
        // elapsed duration (a DST-affected day can be under 24h or over 48h).
        if (calendarDaysDiff === 1) {
            const hours = String(value.getHours()).padStart(2, '0');
            const minutes = String(value.getMinutes()).padStart(2, '0');
            return `Yesterday at ${hours}:${minutes}`;
        }
        return `${calendarDaysDiff} days ago`;
    }

    // Same local calendar day: bucket by elapsed duration.

    // 45–89 seconds => 1 minute ago
    if (diffSec < 90) {
        return '1 minute ago';
    }

    // under 55 minutes => {n} minutes ago
    if (diffSec < 55 * 60) {
        const minutes = Math.min(54, Math.round(diffSec / 60));
        return `${minutes} minutes ago`;
    }

    // 55–89 minutes => 1 hour ago
    if (diffSec < 90 * 60) {
        return '1 hour ago';
    }

    // 90 minutes+ within the same calendar day => {n} hours ago
    const hours = Math.min(23, Math.round(diffSec / 3600));
    return `${hours} hours ago`;
};
